import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Breadcrumb, Button } from 'antd';
import { Link, useNavigate } from 'react-router';

import type { IAdvantage } from '@/api/endpoints/mainPages';
import { useDeleteAdvantageMutation, useMoveAdvantageMutation } from '@/api/endpoints/mainPages/pageBlocks/advantages';
import { DeleteConfirmationModal, DraggableTable } from '@/shared/components';
import { ROUTES, TABLE_TRANSLATIONS } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { CreateAdvantageModal } from './components/Modals/CreateAdvantageModal';
import { EditAdvantageModal } from './components/Modals/EditAdvantageModal';
import { getColumnsData } from './helpers';
import styles from './MainAdvantagesPage.module.scss';
import type { IAdvantageItemData } from './types';

interface IProp {
  advantagesData: IAdvantage[] | [];
  areAdvantagesFetching: boolean;
  pageName: string;
  pageId: number;
}

export const MainAdvantagesPageComponent = ({ advantagesData, areAdvantagesFetching, pageName, pageId }: IProp) => {
  const navigate = useNavigate();

  const [deleteAdvantage, { isLoading: isAdvantageDeleting }] = useDeleteAdvantageMutation();
  const [moveAdvantage] = useMoveAdvantageMutation();

  const sendApiRequest = useApiRequest();

  const [isCreateAdvantageModalOpened, setIsCreateAdvantageModalOpened] = useState(false);
  const [isEditAdvantageModalOpened, setIsEditAdvantageModalOpened] = useState(false);
  const [isDeleteAdvantageModalOpened, setIsDeleteAdvantageModalOpened] = useState(false);

  const openCreateAdvantageModal = useCallback(() => setIsCreateAdvantageModalOpened(true), []);
  const closeCreateAdvantageModal = useCallback(() => setIsCreateAdvantageModalOpened(false), []);

  const openEditAdvantageModal = useCallback(() => setIsEditAdvantageModalOpened(true), []);
  const closeEditAdvantageModal = useCallback(() => setIsEditAdvantageModalOpened(false), []);

  const openDeleteAdvantageModal = useCallback(() => setIsDeleteAdvantageModalOpened(true), []);
  const closeDeleteAdvantageModal = useCallback(() => setIsDeleteAdvantageModalOpened(false), []);

  const [tableData, setTableData] = useState<IAdvantage[]>([]);
  const [currentAdvantageItem, setCurrentAdvantageItem] = useState<null | IAdvantageItemData>(null);

  const setItemToOperate = useCallback((data: IAdvantageItemData | null) => setCurrentAdvantageItem(data), []);

  const handleWithClearOperateItem = (handleFunction?: VoidFunction) => {
    handleFunction?.();
    setItemToOperate(null);
  };

  const columns = useMemo(
    () => getColumnsData({ setItemToOperate, openEditAdvantageModal, openDeleteAdvantageModal }),
    [],
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!tableData || !over) {
      return;
    }

    if (active.id !== over.id) {
      const activeIndex = tableData.findIndex((i) => i.position === active.id);
      const overIndex = tableData.findIndex((i) => i.position === over.id);

      const reorderedArray = arrayMove(tableData, activeIndex, overIndex);

      setTableData(reorderedArray);

      sendApiRequest({
        successMessage: 'Порядок преимуществ успешно изменён',
        apiRequestCallback: () =>
          moveAdvantage({
            mainPageId: pageId,
            advantageId: tableData[activeIndex].id,
            position: Number(overIndex) + 1,
          }),
        errorCallback: () => setTableData(tableData),
      });
    }
  };

  const handleDeleteAdvantage = () => {
    if (currentAdvantageItem) {
      sendApiRequest({
        successMessage: 'Преимущество успешно удалено',
        apiRequestCallback: () =>
          deleteAdvantage({
            mainPageId: pageId,
            advantageId: currentAdvantageItem.id,
          }),
        successCallback: () => handleWithClearOperateItem(closeDeleteAdvantageModal),
      });
    }
  };

  useEffect(() => {
    setTableData(advantagesData);
  }, [advantagesData]);

  return (
    <div className={styles.wrapper}>
      <Breadcrumb
        items={[
          { title: 'Публичные страницы' },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}`}>Главная</Link> },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`}>{pageName}</Link> },
          { title: 'Преимущества школы' },
        ]}
      />
      <Button
        className={styles['create-advantage-button']}
        size="large"
        type="primary"
        onClick={openCreateAdvantageModal}
        disabled={advantagesData.length >= 6}
      >
        Добавить преимущество
      </Button>
      <DraggableTable
        onDragEnd={onDragEnd}
        keysForSorting={tableData.map((i) => i.position)}
        columns={columns}
        rowKey="position"
        size="large"
        locale={TABLE_TRANSLATIONS}
        pagination={false}
        dataSource={tableData}
        loading={areAdvantagesFetching}
      />
      <Button
        className={styles.button}
        size="large"
        type="dashed"
        onClick={() => navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`)}
      >
        Назад
      </Button>
      {isDeleteAdvantageModalOpened && (
        <DeleteConfirmationModal
          isModalOpen={isDeleteAdvantageModalOpened}
          description="Вы уверены, что хотите удалить данное преимущество?"
          title="Удаление преимущества школы"
          handleCancel={() => handleWithClearOperateItem(closeDeleteAdvantageModal)}
          handleOk={handleDeleteAdvantage}
          itemToOperate={currentAdvantageItem}
          shouldDisableElement={isAdvantageDeleting}
        />
      )}
      {isCreateAdvantageModalOpened && (
        <CreateAdvantageModal
          pageId={pageId}
          isModalOpen={isCreateAdvantageModalOpened}
          handleCancel={() => handleWithClearOperateItem(closeCreateAdvantageModal)}
        />
      )}
      {isEditAdvantageModalOpened && currentAdvantageItem && (
        <EditAdvantageModal
          pageId={pageId}
          isModalOpen={isEditAdvantageModalOpened}
          handleCancel={() => handleWithClearOperateItem(closeEditAdvantageModal)}
          handleClearOperateItem={() => handleWithClearOperateItem()}
          itemToOperate={currentAdvantageItem}
        />
      )}
    </div>
  );
};
